import { defineStore } from 'pinia'
import get from 'lodash/get.js'
import orderBy from 'lodash/orderBy.js'
import filter from 'lodash/filter.js'
import find from 'lodash/find.js'
import some from 'lodash/some.js'

import env from '~/libs/env'

import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'

/**
 * Removes 'from american express' and everything after from the card name
 *
 * @param {*} name
 */
// Keep this function in case american express changes thier mind
function formatAmexCardName(name) {
  // Fix blue for business SM
  const serviceMark = name.indexOf('BusinessSM ')
  if (serviceMark) {
    name = name.replace(/BusinessSM /g, 'Business&#8480; ')
  }

  const pos = name.toLowerCase().indexOf(' from american express')
  if (pos > 0) {
    return name.substr(0, pos)
  }
  return name
}

/**
 * Returns sort order for amex cards
 *
 * @param card
 */
function cardSort(card) {
  switch (card.id) {
    // The Blue Business? Plus Credit Card from American Express
    case 'product_2727_1110':
      return 1
    // The Business Platinum® Card from American Express OPEN
    case 'product_2727_92':
      return 3
    // Business Green Rewards Card from American Express OPEN
    case 'product_2727_89':
      return 4
    // The Business Gold Rewards Card from American Express OPEN
    case 'product_2727_333':
      return 6
    // SimplyCash® Plus Business Credit Card from American Express
    case 'product_2727_1043':
      return 7
    // Starwood Preferred Guest® Business Credit Card from American Express
    case 'product_2727_474':
      return 8
    // Platinum Delta SkyMiles®  Business Credit Card from American Express
    case 'product_2727_141':
      return 9
    // Gold Delta SkyMiles®  Business Credit Card from American Express
    case 'product_2727_112':
      return 10
    // The Plum Card® from American Express OPEN
    case 'product_2727_499':
      return 11
    // The Hilton Honors American Express Business Card
    case 'product_2727_1131':
      return 12
    // Delta Reserve for Business Credit Card
    case 'product_2727_756':
      return 13
  }
  return 99
}

function shouldShowAmexDescription(json) {
  const cleanedDescription = json.CurrentPriceDescription.replace('$', '')
  const cleanedPrice = json.CurrentPrice.replace('.00', '')
  const noFee = json.CurrentPriceDescription === 'No annual fee'

  return (
    cleanedDescription !== '' && cleanedDescription !== cleanedPrice && !noFee
  )
}

function shouldShowBankRateDescription(json) {
  const cleanedDescription = json.card_data.fees.annual.display.replace('$', '')
  const cleanedPrice = String(json.card_data.purchases.intro_apr.value).replace(
    '.00',
    ''
  )
  const noFee = json.card_data.fees.annual.display === 'No annual fee'

  return (
    cleanedDescription !== '' && cleanedDescription !== cleanedPrice && !noFee
  )
}

function shouldShowChaseRateDescription(json) {
  const cleanedDescription = (json.attributeClass.Ongoing_Annual_Fee || '')
    .replace('$', '')
    .replace('.00', '')

  const noFee = json.attributeClass.Ongoing_Annual_Fee === 'No annual fee'

  return cleanedDescription !== '' && cleanedDescription !== '0' && !noFee
}

function parseCards(cards = [], borrowerId = null) {
  return cards
    .filter((card) => {
      // since only specific cards can be parsed here, any other cards should be removed first
      return (
        [
          'Chase Ink',
          'Credit One Bank',
          'American Express OPEN',
          'American Express',
          'Chase Bank',
          'Chase',
          'Divvy'
        ].indexOf(card.advertiserName) >= 0
      )
    })
    .map((card) => {
      if (
        card.advertiserName === 'Chase Ink' ||
        card.advertiserName === 'Credit One Bank'
      ) {
        return parseBankRateCard(card)
      } else if (['American Express OPEN', 'American Express'].includes(card.advertiserName)) {
        return parseAmexCard(card, borrowerId)
      } else if (['Chase Bank', 'Chase'].includes(card.advertiserName)) {
        return parseChaseCard(card, borrowerId)
      } else if (card.advertiserName === 'Divvy') {
        return parseDivvyCard(card)
      }
    })
}

function parseBankRateCard(card = {}) {
  const isPureData = Boolean(card.json)
  const json = isPureData ? JSON.parse(card.json) : card

  let bonus = []
  const formattedBullets = []
  if (isPureData) {
    bonus = json.card_data.reward_descriptions.premium_description_short
      ? json.card_data.reward_descriptions.premium_description_short.match(
        '^Earn (.+) bonus (.+)'
      )
      : []

    const bulletsRegex = /<li>(.+?)<\/li>/g
    let match = bulletsRegex.exec(json.bullets)
    while (match != null) {
      formattedBullets.push(match[1])
      match = bulletsRegex.exec(json.bullets)
    }
  }

  return {
    id: isPureData ? card.advertiserId : card.id,
    cardName: card.cardName,
    categories: card.categories,
    advertiserName: card.advertiserName,
    order: card.order,

    annualMemFeeValue: isPureData ? json.card_data.fees.annual.value : card.annualMemberFeeValue,
    showFeeDescription: isPureData ? shouldShowBankRateDescription(json) : card.showFeeDescription,
    annualMemFee: isPureData ? json.card_data.fees.annual.display : card.annualMemberFee,

    introPurchApr: isPureData ? (json.card_data.purchases.intro_apr.value || 0) : card.introPurchaseApr,
    nonIntroPurchApr: isPureData
      ? (json.card_data.purchases.regular_apr.display !== 'N/A'
        ? json.card_data.purchases.regular_apr.display.replace(' Variable', '')
        : 'See Rates')
      : card.nonIntroPurchaseApr,
    aprIsVariable: isPureData ? /Variable/.test(
      json.card_data.purchases.regular_apr.display
    ) : card.aprIsVariable,

    bullets: isPureData ? formattedBullets : card.bullets,

    rewardsBonus: isPureData
      ? (json.card_data.reward_descriptions.premium_description_short
        ? bonus[1]
        : 'N/A')
      : card.rewardsBonus,
    rewardsBonusText: isPureData
      ? (bonus[1] && (bonus[1].indexOf('$') > -1 || bonus[1] === 'N/A')
        ? 'Rewards Bonus'
        : 'Bonus Points')
      : card.rewardsBonusText,

    terms: '',
    apply: isPureData ? card.clickUrl : card.apply,
    image: isPureData ? card.imageUrl : card.image,

    clickedAt: card.clickedAt || null,

    secureMessage: `On ${card.advertiserName}'s secure website. Terms apply.`
  }
}

// {"Id":"product_2727_92","CatalogId":"2727","CampaignId":"7411","CampaignName":"American Express OPEN","CatalogItemId":"92","Name":"The Business Platinum Card\u00ae from American Express","Description":"","Bullets":["Welcome Offer: Earn 100,000 Membership Rewards\u00ae points after you spend $15,000 on eligible purchases with the Business Platinum Card within the first 3 months of Card Membership. "," Get 50% more Membership Rewards\u00ae points. That's an extra half point per dollar, on each eligible purchase of $5,000 or more. You can get up to 1 million additional points per year. "," 35% Airline Bonus: Use Membership Rewards\u00ae Pay with Points for all or part of a flight with your selected qualifying airline, and you can get 35% of the points back, up to 500,000 bonus points per calendar year when you book on amextravel.com. "," Get 5X Membership Rewards\u00ae points on flights and prepaid hotels on amextravel.com. "," Enroll to get up to $200 in statement credits annually by getting up to $100 semi-annually for U.S. purchases with Dell. Terms apply. "," PAY OVER TIME OPTION: A flexible payment option to help manage cash flow on purchases of $100 or more. "," Terms Apply."],"Labels":["Business"," Excellent Credit"," Good Credit"," Charge"," Travel"," Airlines"," Hotels"," No Foreign Transaction Fees"," Points"," Rewards "],"Manufacturer":"American Express","Url":"https:\/\/goto.americanexpress.com\/c\/1204093\/420898\/7411?prodsku=92&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Famerican-express-business-platinum-credit-card-amex%2F42732%2F%3Flinknav%3DUS-Acq-GCP-BusinessCards-ViewAllCards-LearnMore-Platinum&intsrc=APIG_2727","MobileUrl":"","ImageUrl":"https:\/\/member.impactradius.com\/display-ad\/7411-441830?v=2&fromPopup=1","CurrentPrice":"595.00","Currency":"USD","LaunchDate":"","ExpirationDate":"","MobileDescription":"","ProductType":"Business","CreditRating":["Good","Excellent"],"CreditRatingDescription":"","ProcessingNetwork":"AMEX","CurrentPriceDescription":"$595","IntroPurchaseApr":"","IntroPurchaseAprType":"N\/A","IntroPurchaseAprDescription":"N\/A","IntroPurchaseAprPeriod":"","IntroPurchaseAprPeriodType":"","IntroPurchaseAprPeriodDescription":"N\/A","NonIntroPurchaseApr":"14.24;22.24","NonIntroPurchaseAprType":"Variable","NonIntroPurchaseAprDescription":"14.24% - 22.24% Variable","PurchaseGracePeriod":"","IntroTransferApr":"","IntroTransferAprType":"N\/A","IntroTransferAprDescription":"N\/A","IntroTransferAprPeriod":"","IntroTransferAprPeriodType":"","IntroTransferAprPeriodDescription":"N\/A","NonIntroTransferApr":"","NonIntroTransferAprType":"N\/A","NonIntroTransferAprDescription":"N\/A","TransferGracePeriod":"","CashAdvanceApr":"","CashAdvanceAprDescription":"N\/A","CashGracePeriod":"","PenaltyApr":"29.24","PenaltyAprDescription":"29.24%\u00a0Variable","BalanceTransferFee":"","BalanceTransferFeeDescription":"N\/A","CashAdvanceFee":"","CashAdvanceFeeDescription":"N\/A","LatePaymentFee":"","LatePaymentFeeDescription":"$39\u00a0or\u00a02.99%\u00a0of any past due Pay in Full amount, whichever is greater.","ReturnCheckFee":"","ReturnCheckFeeDescription":"$39","OverlimitFee":"","OverlimitFeeDescription":"N\/A","ForeignTransactionFee":"","ForeignTransactionFeeDescription":"None","MinimumDeposit":"","RewardsType":"","RewardsBonus":"","RewardsRate":"","RewardsType2":"","RewardsBonus2":"","RewardsRate2":"","RewardsType3":"","RewardsBonus3":"","RewardsRate3":"","AvailableApplications":[],"Terms":"https:\/\/www.americanexpress.com\/us\/credit-cards\/card-application\/apply\/prospect\/terms\/business-platinum-charge-card\/42732-AJTnC-0?rwdFlag=rwd\/?rwdFlag=rwd#FeeTable","BonusOffer":" 100,000 ","BonusOfferTerms":"Earn 100,000 Membership Rewards\u00ae points after you spend $15,000 on eligible purchases with the Business Platinum Card within the first 3 months of Card Membership. ","ProhibitedCategories":"Low Interest , Balance Transfer , 0% APR , Student , Prepaid , Secured , Instant Approval , Top Credit Cards","Text1":"","Text2":"","Text3":"","Numeric1":"","Numeric2":"","Numeric3":"","Money1":"","Money2":"","Money3":"","Uri":"\/Mediapartners\/IR222oziWEhp1204093efkKrABPxBmS9P1\/FinancialCatalogs\/2727\/Items\/product_2727_92"}

function parseAmexCard(card = {}, borrowerId = null) {
  const isPureData = Boolean(card.json)
  const json = isPureData ? JSON.parse(card.json) : card
  const subId3Param = borrowerId ? `&subId3=${borrowerId}` : ''
  const idToUrlMap = {
    // Band-aid fixing the links with new links while I pass up the line that I'd like to have these stored in the db
    product_2727_1110: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP1${subId3Param}&sharedid=SBS30020&prodsku=1110&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Famerican-express-blue-business-plus-credit-card-amex%2F42732%3Feep%3D42732&intsrc=PUI1_2727`,
    product_2727_112: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP6${subId3Param}&sharedid=SBS30020&prodsku=112&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Fdelta-gold-card-skymiles-credit-card%2F42732%3Flinknav%3DUS-Acq-GCP-Open-CompareCards-LearnMore-DeltaGold&intsrc=PUI1_2727`,
    product_2727_1131: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP7${subId3Param}&sharedid=SBS30020&prodsku=1131&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Fhilton-honors-american-express-business-credit-card-amex%2F42732%3Feep%3D42732&intsrc=PUI1_2727`,
    product_2727_1142: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP11${subId3Param}&sharedid=SBS30020&prodsku=1142&u=https%3A%2F%2Fwww.amazon.com%2Fdp%2FB07984JN3L%3Fplattr%3D%7Bmp_value1%7D&intsrc=PUI1_2727`,
    product_2727_1143: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP12${subId3Param}&sharedid=SBS30020&prodsku=1143&u=https%3A%2F%2Fwww.amazon.com%2Fdp%2FB07984JN3L%3Fplattr%3D%7Bmp_value1%7D&intsrc=PUI1_2727`,
    product_2727_1157: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP3${subId3Param}&sharedid=SBS30020&prodsku=1157&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Famerican-express-business-gold-card-amex%2F42732%3Feep%3D42732&intsrc=PUI1_2727`,
    product_2727_1169: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP2${subId3Param}&sharedid=SBS30020&prodsku=1169&u=https%3A%2F%2Fwww.americanexpress.com%2Fen-us%2Fbusiness%2Fcredit-cards%2Fblue-business-cash%2F42732%3Feep%3D42732&intsrc=PUI1_2727`,
    product_2727_141: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP8${subId3Param}&sharedid=SBS30020&prodsku=141&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Fdelta-platinum-credit-card%2F42732%3Flinknav%3DUS-Acq-GCP-BusinessCards-ViewAllCards-LearnMore-DeltaPlatinum&intsrc=PUI1_2727`,
    product_2727_474: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP9${subId3Param}&sharedid=SBS30020&prodsku=474&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Famex-marriott-bonvoy-business-credit-card%2F42732%3Feep%3D42732&intsrc=PUI1_2727`,
    product_2727_499: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP10${subId3Param}&sharedid=SBS30020&prodsku=499&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Famerican-express-plum-card-amex%2F42732%3Feep%3D42732&intsrc=PUI1_2727`,
    product_2727_756: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP5${subId3Param}&sharedid=SBS30020&prodsku=756&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Fdelta-skymiles-reserve%2F42732%3Flinknav%3DUS-Acq-GCP-Open-CompareCards-LearnMore-DeltaReserve&intsrc=PUI1_2727`,
    product_2727_92: `https://goto.americanexpress.com/c/1204093/420898/7411?subId1=BP&subId2=AXP4${subId3Param}&sharedid=SBS30020&prodsku=92&u=https%3A%2F%2Fwww.americanexpress.com%2Fus%2Fcredit-cards%2Fbusiness%2Fbusiness-credit-cards%2Famerican-express-business-platinum-credit-card-amex%2F42732%3Feep%3D42732&intsrc=PUI1_2727`
  }

  const buildUrl = (clickUrl, removePrevParams = false) => {
    const url = new URL(clickUrl)
    const tackingParams = `subId1=BP${
      borrowerId ? `&subId3=${borrowerId}` : ''
    }&sharedid=SBS30020`

    if (!url.search) {
      url.search = `?${tackingParams}`
    } else {
      if (removePrevParams) {
        url.search.replace(/&?(subId[1-3]|sharedid)=([^&]*)&?/g, '')
      }
      url.search = `?${tackingParams}&${url.search.replace(/^\?/, '')}`
    }

    return url.toString()
  }

  const cardId = isPureData ? card.advertiserId : card.id
  const advertiserName = card.advertiserName.replace(' OPEN', '')
  return {
    id: cardId,
    cardName: card.cardName,
    categories: card.categories,
    advertiserName: advertiserName,
    order: card.order,

    annualMemFeeValue: isPureData ? json.CurrentPrice : card.annualMemberFeeValue,
    showFeeDescription: isPureData ? shouldShowAmexDescription(json) : card.showFeeDescription,
    annualMemFee: isPureData ? json.CurrentPriceDescription : card.annualMemberFee,

    introPurchApr: isPureData ? json.IntroPurchaseApr || 0 : card.introPurchaseApr,
    nonIntroPurchApr: isPureData
      ? (json.NonIntroPurchaseAprDescription !== 'N/A'
        ? json.NonIntroPurchaseAprDescription.replace(' Variable', '')
        : 'See Rates')
      : card.nonIntroPurchaseApr,
    aprIsVariable: isPureData ? /Variable/.test(json.NonIntroPurchaseAprDescription) : card.aprIsVariable,

    bullets: isPureData ? json.Bullets : card.bullets,

    rewardsBonus: isPureData ? (json.BonusOffer || 'N/A') : card.rewardsBonus,
    rewardsBonusText: 'Welcome Bonus Offer',

    terms: isPureData ? json.Terms : card.terms,
    apply: idToUrlMap[cardId] || (isPureData ? buildUrl(json.Url) : buildUrl(card.apply, true)), // We retrieve their url from the url map, or return the clickUrl if it is not in the map.
    image: isPureData ? card.imageUrl : card.image,

    clickedAt: card.clickedAt || null,

    secureMessage: `On ${advertiserName}' secure website. Terms apply.`
  }
}

function parseChaseCard(card = {}) {
  const isPureData = Boolean(card.json)
  const json = isPureData ? JSON.parse(card.json) : card

  const bonus = isPureData
    ? (json.description.short
      ? json.description.short.replace(/[^0-9.,$]/g, '')
      : 'N/A')
    : card.rewardsBonus
  const showFeeDescription = isPureData ? shouldShowChaseRateDescription(json) : card.showFeeDescription

  const formattedBullets = isPureData ? [] : card.bullets
  if (isPureData) {
    const bulletsRegex = /<li>(.+?)<\/li>/g
    let match = bulletsRegex.exec(json.attributeClass.Marketing_Bullets)
    while (match != null) {
      formattedBullets.push(match[1])
      match = bulletsRegex.exec(json.attributeClass.Marketing_Bullets)
    }
  }

  const advertiserName = card.advertiserName.replace(' Bank', '')
  return {
    id: isPureData ? card.advertiserId : card.id,
    cardName: card.cardName,
    categories: card.categories,
    advertiserName,
    order: card.order,
    apply: isPureData ? card.clickUrl : card.apply,
    image: isPureData ? card.imageUrl : card.image,

    annualMemFeeValue: isPureData
      ? (json.attributeClass.Ongoing_Annual_Fee || '').replace(
          /[^0-9.]/g,
          ''
        )
      : card.annualMemberFeeValue,
    showFeeDescription,
    annualMemFee: isPureData
      ? (showFeeDescription
        ? json.attributeClass.Ongoing_Annual_Fee
        : 'No annual fee')
      : card.annualMemberFee,

    introPurchApr: isPureData
      ? ((json.attributeClass.Intro_Purchase_Apr || 'N/A') === 'N/A'
        ? 0
        : json.attributeClass.Intro_Purchase_Apr)
      : card.introPurchaseApr,
    nonIntroPurchApr: isPureData
      ? (json.attributeClass.Ongoing_Purchase_APR !== 'N/A'
          ? json.attributeClass.Ongoing_Purchase_APR.replace(
              /(^\D*)(\d+)/g,
              '$2'
            ).replace(' Variable', '')
          : 'See Rates')
      : card.nonIntroPurchaseApr,
    aprIsVariable: isPureData ? /Variable/.test(json.attributeClass.Ongoing_Purchase_APR) : card.aprIsVariable,

    bullets: formattedBullets,

    rewardsBonus: bonus,
    rewardsBonusText:
      bonus && (bonus.indexOf('$') > -1 || bonus === 'N/A')
        ? 'Rewards Bonus'
        : 'Bonus Points',

    terms: isPureData
      ? (json.attributeClass.TermsURL === 'N/A'
        ? ''
        : json.attributeClass.TermsURL)
      : card.terms,

    clickedAt: card.clickedAt || null,

    secureMessage: `On ${advertiserName}'s secure website. Terms apply.`
  }
}

function parseDivvyCard(card = {}) {
  const isPureData = Boolean(card.json)
  const json = isPureData ? JSON.parse(card.json) : card

  const mainDescription = []

  if (json.title) {
    mainDescription.push(`<p class="fw-bold">${json.title}</p>`)
  }

  if (Array.isArray(json.descriptionItems) && json.descriptionItems.length) {
    mainDescription.push(`<p>${json.descriptionItems[0]}</p>`)
  }

  return {
    id: isPureData ? card.advertiserId : card.id,
    cardName: card.cardName,
    categories: card.categories,
    advertiserName: card.advertiserName,
    order: card.order,
    apply: isPureData ? card.clickUrl : card.apply,
    image: isPureData ? card.imageUrl : card.image,

    mainDescription: mainDescription.join(''),
    descriptionItems: Array.isArray(json.descriptionItems)
      ? json.descriptionItems.slice(1)
      : [],
    bullets: json.bullets,

    terms: '',

    clickedAt: card.clickedAt || null,

    secureMessage: `On ${card.advertiserName}'s secure website. Terms apply.`
  }
}

function rewardsBonusText(json = {}) {
  const isCredit = get(json, 'rewardsBonus', '').indexOf('$') !== -1
  const isPoints = get(json, 'rewardsBonusTerms', '').indexOf('points') !== -1
  const isMiles = get(json, 'rewardsBonusTerms', '').indexOf('miles') !== -1

  if (isCredit) {
    return json.rewardsBonus
  } else if (isPoints) {
    return json.rewardsBonus + ' Points'
  } else if (isMiles) {
    return json.rewardsBonus + ' Miles'
  }

  return 'N/A'
}

function parseBrexCard(card = {}) {
  return {
    id: 'brex-1',
    cardName: 'Brex Cash',
    categories: [
      {
        slug: 'lendio',
        pivot: { order: 1, tagTitle: 'Best Small Business Solution' }
      }
    ],
    advertiserName: 'Brex',
    order: 1,

    annualMemFee: 'No annual fee, interest, or minimums',
    showFeeDescription: false,
    annualMemFeeValue: '0.00',

    introPurchApr: 0,
    nonIntroPurchApr: 'Zero Interest',
    aprIsVariable: false,

    descriptionItems: [
      'Spend, save, and control your money with Brex — A business credit card, cash account, and expense tools in one place.',
      'Earn a $100 bonus* with qualifying activities.',
      'Here are a few reasons why we recommend Brex to our customers:'
    ],

    bullets: [
      '<span class="fw-bold">Convenient account:</span> Make deposits and payments; send free international and domestic wires, ACHs, and checks, and pay no monthly fees or interest.',
      '<span class="fw-bold">Rewards:</span> Earn up to 8% back on all card purchases, including 5% on Microsoft ads, 1.5% on all other ad spend, and 3% on Apple products. ',
      '<span class="fw-bold">Perks & discounts:</span> Save up to $150,000, including $5,000 in AWS credits, $1,500 in Snap ads, and $150 in Google Ads credits.',
      '<span class="fw-bold">No fees or personal guarantee:</span> Brex is 100% free, and there’s no personal guarantee, so your own funds won’t be on the line if you can’t pay your Brex card.',
      '<span class="fw-bold">Builds business credit:</span> Card payments get reported to the credit bureaus to build your business credit, which makes it easier to borrow in the future.'
    ],

    rewardsBonus: '10,000 Brex points',
    rewardsBonusText: 'Rewards Bonus',

    terms: '',
    apply: 'https://www.brex.com/partners/lendio/?campaign_id=product_cctab',
    image: '/images/opt-in-brex-cc.svg',

    secureMessage: `On Brex's secure website. Terms apply.`
  }
}

export const useCreditCardsStore = defineStore('creditCards', () => {
  const nuxtApp = useNuxtApp()
  const { $axios } = nuxtApp

  const borrowerStore = useBorrowerStore()
  const rootStore = useRootStore()


/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const creditCards = ref(null)
  const creditCard = ref(null)
  const creditCardCategories = ref(null)
  const categoriesLoading = ref(false)
  const filteredCards = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const warning = ref(null)
  const loaded = ref(false)

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/


/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

    function fillCreditCardAltOffers(payload = {}) {
      const borrowerId = get(borrowerStore, 'borrower.id')

      loading.value = true
      const cards = parseCards(payload, borrowerId).sort(
        (a, b) => cardSort(a) - cardSort(b)
      )

      this.creditCardsSuccess(cards)
    }

    function singleCreditCardSuccess(card) {
      loading.value = false
      creditCard.value = card
    }

    function creditCardsSuccess(cards, addMore = false) {
      let allCards = cards
      if (!addMore){
        loading.value = false
      } else {
        allCards = [...(creditCards.value || []), ...cards]
      }

      const ordered = orderBy(allCards, (card) => card.order)
      // ordered.unshift(parseBrexCard()) - we don't support brex
      creditCards.value = ordered
    }

    function filterCardsSuccess(cards) {
      // Check for query param to inialize filtered cards
      let category = get(useRouter(), 'currentRoute.query.category')
      if (category) {
        // Find cards in category
        cards = filter(cards, (card) =>
          some(card.categories, { slug: category })
        )
        // order by category pivot order
        cards = orderBy(
          cards,
          (card) => find(card.categories, { slug: category }).pivot.order
        )
      }
      // cards.unshift(parseBrexCard()) - we don't support brex
      filteredCards.value = cards
      loading.value = false
    }

    async function getCreditCardOffers(
      payload = { ignoreMinimumScore: false, force: false }
    ) {
      const creditScore = get(
        borrowerStore,
        'borrowerValues.creditScore.value'
      )
      const monthInBusiness = borrowerStore.getMonthsInBusiness
      const averageMonthlySales = parseInt(
        get(
          borrowerStore,
          'borrowerValues.average_monthly_sales.value'
        )
      )
      let annualRevenue = parseInt(
        get(
          borrowerStore,
          'borrowerValues.average_monthly_sales_calculated.value'
        )
      )
      if (isNaN(annualRevenue) || !annualRevenue) {
        annualRevenue = averageMonthlySales > 0 ? averageMonthlySales * 12 : 0
      }

      const borrowerId = get(borrowerStore, 'borrower.id')
      if (creditScore && creditScore < 600) {
        if (payload.ignoreMinimumScore) {
          warning.value =
            "Your credit score isn't quite high enough for these credit card options, however you can still apply to see more options."
        } else {
          warning.value = 'Credit score below minimum of 600 for credit cards'
          loading.value = false
          return
        }
      }

      if (creditCards.value && loaded.value && !payload.force) {
        return creditCards.value
      }
      loading.value = true
      const res = await $axios
        .get(env('apiUrl') + '/credit-cards', {
          token: rootStore.authToken,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .catch((err) => {
          log.error('Failed to load credit card options', { err })
          loading.value = false
          error.value = err
          return
        })

      if (!res) {
        return []
      } else {
        let cards = orderBy(
          parseCards(res.data.data, borrowerId),
          (card) => card.order
        )

        const showDivvyCard =
          creditScore >= 650 && monthInBusiness >= 24 && annualRevenue >= 500000

        // only show AMEX card options for borrowers w credit score 699 or higher
        // And do not show the card with id 22969920
        // only show Divvy card options for borrowers w
        // - credit score >= 650 and
        // - months in business >= 24 and
        // - annual revenue >= 500000
        cards = cards.filter((card) => {
          if (card.id !== '22969920') {
            if (card.advertiserName === 'Divvy') return showDivvyCard
            else if (creditScore > 699) return true
            else return card.advertiserName !== 'American Express'
          } else return false
        })
        this.creditCardsSuccess(cards)
        this.filterCardsSuccess(cards)
        loaded.value = true
        return res.data.data
      }
    }

    async function getCreditCard(advertiserId) {
      if (
        creditCard.value &&
        creditCard.value.advertiserId &&
        creditCard.value.advertiserId === advertiserId
      ) {
        return creditCard.value
      }
      loading.value = true
      const res = await $axios
        .get(env('apiUrl') + '/credit-card/' + advertiserId, {
          token: rootStore.authToken,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .catch((err) => {
          log.error('Failed to load credit card options', { err })
          loading.value = false
          error.value = err
          return
        })
      if (!res) {
        return {}
      } else {
        const advertiserName = get(res, 'data.data.advertiserName')
        let parsedCard = {}
        if (advertiserName) {
          const card = get(res, 'data.data')
          if (
            advertiserName === 'Chase Ink' ||
            advertiserName === 'Credit One Bank'
          ) {
            parsedCard = parseBankRateCard(card)
          } else if (advertiserName === 'American Express OPEN') {
            parsedCard = parseAmexCard(card)
          } else if (advertiserName === 'Chase Bank') {
            parsedCard = parseChaseCard(card)
          } else if (advertiserName === 'Divvy') {
            parsedCard = parseDivvyCard(card)
          }
        }
        this.singleCreditCardSuccess(parsedCard)
        return parsedCard
      }
    }

    async function getCreditCardCategories() {
      if (creditCardCategories.value) {
        return creditCardCategories.value
      }
      categoriesLoading.value = true
      const res = await $axios
        .get(env('apiUrl') + '/credit-card-categories', {
          token: rootStore.authToken,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .catch((err) => {
          log.error('Failed to load credit card options', { err })
          loading.value = false
          error.value = err
          return
        })
      if (!res) {
        return []
      } else {
        creditCardCategories.value = get(res, 'data.data')
        categoriesLoading.value = false
        return res.data.data
      }
    }

    function filterCards(cards) {
      loading.value = true
      this.filterCardsSuccess(cards)
      return cards
    }

    function addCreditCards(cards) {
      const existingIds = (creditCards.value || []).map((card) => card.id)
      const newCards = (cards || []).filter(card => !existingIds.includes(card.advertiserId || card.id))
      const borrowerId = get(borrowerStore, 'borrower.id')
      const parsedCards = parseCards(newCards, borrowerId)

      if (parsedCards.length) {
        this.creditCardsSuccess(parsedCards, true)
      }
    }

  async function creditCardCtaClick(creditCardId) {
    try {
      const res = await $axios
        .post(
          `${env('apiUrl')}/credit-card-interested/${creditCardId}/borrower/${
            borrowerStore.borrowerId
          }`,
          null,
          { headers: { 'Content-Type': 'application/json' } }
        )
      const interaction = res?.data?.data

      const clickedTime = interaction?.clickedOnCallToAction ?? (new Date()).toISOString()
      const creditCard = creditCards.value.find((creditCard) => creditCard.id === creditCardId)
      if (creditCard) {
        creditCard.clickedAt = clickedTime
      }
    } catch (err) {
      log.error('Failed to mark credit card as interested', {
        creditCardId,
        err
      })
    }
  }

    return {
      // STATE
      creditCards,
      creditCard,
      creditCardCategories,
      categoriesLoading,
      filteredCards,
      loading,
      error,
      warning,
      loaded,

      // GETTERS

      // ACTIONS
      fillCreditCardAltOffers,
      singleCreditCardSuccess,
      creditCardsSuccess,
      filterCardsSuccess,
      getCreditCardOffers,
      getCreditCard,
      getCreditCardCategories,
      filterCards,
      addCreditCards,
      creditCardCtaClick,
    }
})
